import { useMutation, useQuery } from '@tanstack/react-query';

import API from '@api/API';

import { Entertainer } from '@queries/EntertainerQueries';

export type ColoradoDragGuideQuoteSource =
  | {
      type: 'seriesFeatureApplication::v0';
      documentId: string;
      documentKey: 'value.questions.spotlightOne' | 'value.questions.spotlightTwo';
    }
  | {
      type: 'post';
      documentId: string;
    };

export interface EntertainerQuoteReview<QuoteSource> {
  createdAt?: string;
  text: string;
  question?: string;
  userId: string;
  entertainerId: string;
  source: QuoteSource;
  quoteByEntertainer?: Entertainer;
  quoteId?: string;
  curatedQuote?: {
    quote: string;
    photoCredits?: string;
    photoMediaId?: string;
    quoteByEntertainerId?: string;
    quoteByEntertainerName?: string;
    quoteByEntertainerPhotoMediaId?: string;
    status?: string;
  };
}

export type ColoradoDragGuideQuoteReview = EntertainerQuoteReview<ColoradoDragGuideQuoteSource>;

export interface EntertainerQuotes<QuoteSource> {
  entertainer: Entertainer;
  entertainerFollowerCount: number;
  quotesAboutEntertainer: EntertainerQuoteReview<QuoteSource>[];
}

export type ColoradoDragGuideEntertainerQuotes = EntertainerQuotes<ColoradoDragGuideQuoteSource>;

export const useColoradoDragGuideQuotesQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useColoradoDragGuideQuotesQuery.queryKey(),
    queryFn: () =>
      API.get<{ quotes: ColoradoDragGuideEntertainerQuotes[] }>(
        '/v1/super-admin/colorado-drag-guide-quotes',
      ),
  });

  return {
    coloradoDragGuideQuotesAreReady: isFetched,
    coloradoDragGuideQuotes: data?.quotes,
    refetchColoradoDragGuideQuotes: refetch,
  };
};
useColoradoDragGuideQuotesQuery.queryKey = () => ['coloradoDragGuideQuotes'];

export interface CreateEntertainerQuotePayload<QuoteSource> {
  entertainerId: string;
  quote: string;
  quoteByEntertainerId?: string;
  quoteByEntertainerName?: string;
  quoteByEntertainerPhotoMediaId?: string;
  photoCredits?: string;
  photoMediaId?: string;
  source: QuoteSource;
}

export type CreateColoradoDragGuideQuotePayload =
  CreateEntertainerQuotePayload<ColoradoDragGuideQuoteSource>;

export const useCreateColoradoDragGuideEntertainerQuoteMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: CreateColoradoDragGuideQuotePayload) =>
      API.post<{ quote: { _id: string } }, CreateColoradoDragGuideQuotePayload>(
        '/v1/super-admin/colorado-drag-guide-quotes',
        payload,
      ),
  });

  return { isCreatingQuote: isPending, createQuoteAsync: mutateAsync };
};

export type UpdateEntertainerQuotePayload<QuoteSource> = Partial<
  Omit<CreateEntertainerQuotePayload<QuoteSource>, 'entertainerId'>
> & {
  quoteId: string;
  photo?: string;
};

export type UpdateColoradoDragGuideQuotePayload =
  UpdateEntertainerQuotePayload<ColoradoDragGuideQuoteSource>;

export const useUpdateColoradoDragGuideEntertainerQuoteMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ quoteId, ...payload }: UpdateColoradoDragGuideQuotePayload) =>
      API.put('/v1/super-admin/colorado-drag-guide-quotes', { quoteId, ...payload }),
  });

  return { isUpdatingQuote: isPending, updateQuoteAsync: mutateAsync };
};

export interface ScheduleEntertainerQuotePayload {
  quoteId: string;
  quoteFontSize: number;
}

export const useScheduleColoradoDragGuideEntertainerQuoteMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ quoteId, quoteFontSize }: ScheduleEntertainerQuotePayload) =>
      API.post(`/v1/super-admin/colorado-drag-guide-quotes/${quoteId}/schedule`, {
        quoteFontSize,
      }),
  });

  return { isSchedulingQuote: isPending, scheduleQuoteAsync: mutateAsync };
};

export type BookDragKingsQuoteSource =
  | {
      type: 'dragKingCommunity::Spotlight';
      documentId: string;
    }
  | {
      type: 'bookDragKingsFeatureApplication::v0';
      documentId: string;
      documentKey: `value.questions.${'whyBookKings' | 'aboutKingsForCommunity' | 'aboutEntertainer' | 'aboutScene'}`;
    }
  | {
      type: 'post';
      documentId: string;
    };

export type BookDragKingsQuoteReview = EntertainerQuoteReview<BookDragKingsQuoteSource>;

export type BookDragKingsEntertainerQuotes = EntertainerQuotes<BookDragKingsQuoteSource>;

export const useBookDragKingsQuotesQuery = () => {
  const { isFetched, data, refetch } = useQuery({
    queryKey: useBookDragKingsQuotesQuery.queryKey(),
    queryFn: () =>
      API.get<{ quotes: BookDragKingsEntertainerQuotes[] }>(
        '/v1/super-admin/book-drag-kings-quotes',
      ),
  });

  return {
    bookDragKingsQuotesAreReady: isFetched,
    bookDragKingsQuotes: data?.quotes,
    refetchBookDragKingsQuotes: refetch,
  };
};
useBookDragKingsQuotesQuery.queryKey = () => ['bookDragKingsQuotes'];

export type CreateBookDragKingsQuotePayload =
  CreateEntertainerQuotePayload<BookDragKingsQuoteSource>;

export const useCreateBookDragKingsEntertainerQuoteMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: CreateBookDragKingsQuotePayload) =>
      API.post<{ quote: { _id: string } }, CreateBookDragKingsQuotePayload>(
        '/v1/super-admin/book-drag-kings-quotes',
        payload,
      ),
  });

  return { isCreatingQuote: isPending, createQuoteAsync: mutateAsync };
};

export type UpdateBookDragKingsQuotePayload =
  UpdateEntertainerQuotePayload<BookDragKingsQuoteSource>;

export const useUpdateBookDragKingsEntertainerQuoteMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ quoteId, ...payload }: UpdateBookDragKingsQuotePayload) =>
      API.put('/v1/super-admin/book-drag-kings-quotes', { quoteId, ...payload }),
  });

  return { isUpdatingQuote: isPending, updateQuoteAsync: mutateAsync };
};

export const useScheduleBookDragKingsEntertainerQuoteMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: ({ quoteId, quoteFontSize }: ScheduleEntertainerQuotePayload) =>
      API.post(`/v1/super-admin/book-drag-kings-quotes/${quoteId}/schedule`, {
        quoteFontSize,
      }),
  });

  return { isSchedulingQuote: isPending, scheduleQuoteAsync: mutateAsync };
};

export interface AddEntertainerToDragKingCommunitySpotlightsPayload {
  documentIds: string[];
  instagramHandle: string;
}

export const useAddEntertainerToDragKingCommunitySpotlightsMutation = () => {
  const { isPending, mutateAsync } = useMutation({
    mutationFn: (payload: AddEntertainerToDragKingCommunitySpotlightsPayload) =>
      API.put('/v1/super-admin/book-drag-kings-spotlights/entertainer', payload),
  });

  return {
    isAddingEntertainerInstagramHandle: isPending,
    addEntertainerInstagramHandleAsync: mutateAsync,
  };
};
